/**
 * PATH: packages/job-hub/src/jobs/qa/qaInspectionApiExtras.ts
 * FILE_ID: JOBHUB-QA-INSPECTION-API-EXTRAS-001
 * PURPOSE: Standard-check and row-undo calls that do not fit in qaInspectionApi.ts.
 */
import type { JobHubHttpClient } from '../jobHubDataAdapter'
import type { QaItemChangeRequest, QaItemInstance } from './qaTypes'

export type QaStandardCheckSet = {
  key: string
  title: string
  count: number
  items: { title: string }[]
}

export type QaStandardCheckAdd = {
  created: QaItemInstance[]
  skipped: number
}

export function createQaInspectionExtras(
  client: JobHubHttpClient,
  headers: Record<string, string> | undefined,
) {
  return {
    async listStandardChecks() {
      const response = await client.get('/api/qa/standard-checks/', { headers })
      return (Array.isArray(response.data) ? response.data : []) as QaStandardCheckSet[]
    },
    async addStandardChecks(checklistId: string, key: string) {
      const response = await client.post(
        `/api/qa/checklist-instances/${checklistId}/add-standard-checks/`,
        { key },
        { headers },
      )
      return response.data as QaStandardCheckAdd
    },
    async restoreRowDeletion(deletionId: string) {
      const response = await client.post(
        `/api/qa/row-deletions/${deletionId}/restore/`,
        {},
        { headers },
      )
      return response.data as { restored: boolean; area_id: string }
    },
    async renameFloor(floorId: string, name: string) {
      const response = await client.patch(`/api/qa/floors/${floorId}/`, { floor_name: name }, { headers })
      return response.data
    },
    async reorderFloors(order: { id: string; sort_order: number }[]) {
      await client.post('/api/qa/floors/reorder/', { order }, { headers })
    },
    async createFloor(inspectionId: string, name: string, sortOrder: number) {
      const response = await client.post('/api/qa/floors/', {
        inspection: inspectionId,
        floor_name: name,
        sort_order: sortOrder,
      }, { headers })
      return response.data as { id: string }
    },
    async duplicateFloor(floorId: string) {
      const response = await client.post(`/api/qa/floors/${floorId}/duplicate/`, {}, { headers })
      return response.data as { id: string }
    },
    async deleteFloor(floorId: string) {
      await client.delete(`/api/qa/floors/${floorId}/`, { headers })
    },
    async listGroupNames(jobId: number) {
      const response = await client.get('/api/qa/floors/group-names/', {
        params: { job_id: jobId },
        headers,
      })
      const names = (response.data as { names?: string[] } | undefined)?.names
      return Array.isArray(names) ? names : []
    },
    async deleteItemForUndo(itemInstanceId: string) {
      const response = await client.delete(`/api/qa/item-instances/${itemInstanceId}/`, {
        headers,
        params: { undo: 1 },
      })
      return response.data as QaItemChangeRequest | { status?: string; deletion_id?: string } | undefined
    },
  }
}
